/**
 * Assistant/Workflows Service - manages AI-generated suggestions and workflow automation
 */

import type { AssistantSuggestion, SuggestionType } from '../types';
import {
  getStore,
  addEntity,
  getEntity,
  getEntitiesByWorkspace,
  updateEntity,
  deleteEntity,
} from '../store';
import { generateId } from '../id';
import { getTasksByWorkspace, getOverdueTasks, getPriorityTasks } from './tasks.service';
import { getContactsByStage } from './crm.service';

export interface CreateSuggestionInput {
  workspaceId: string;
  type: SuggestionType;
  title: string;
  description: string;
  actionType?: string;
  actionPayload?: Record<string, unknown>;
}

export function createSuggestion(input: CreateSuggestionInput): AssistantSuggestion {
  const now = new Date();
  const suggestion: AssistantSuggestion = {
    id: generateId('sug'),
    workspaceId: input.workspaceId,
    type: input.type,
    title: input.title,
    description: input.description,
    actionType: input.actionType,
    actionPayload: input.actionPayload,
    dismissed: false,
    createdAt: now,
    updatedAt: now,
  };

  return addEntity(getStore().suggestions, suggestion);
}

export function getSuggestion(id: string): AssistantSuggestion | undefined {
  return getEntity(getStore().suggestions, id);
}

export function getSuggestionsByWorkspace(workspaceId: string): AssistantSuggestion[] {
  return getEntitiesByWorkspace(getStore().suggestions, workspaceId);
}

export function getActiveSuggestions(workspaceId: string): AssistantSuggestion[] {
  return getSuggestionsByWorkspace(workspaceId).filter((s) => !s.dismissed);
}

export function dismissSuggestion(id: string): AssistantSuggestion | undefined {
  return updateEntity(getStore().suggestions, id, { dismissed: true });
}

export function deleteSuggestion(id: string): boolean {
  return deleteEntity(getStore().suggestions, id);
}

/**
 * Generate contextual suggestions based on workspace data
 */
export function generateWorkspaceSuggestions(workspaceId: string): AssistantSuggestion[] {
  const suggestions: AssistantSuggestion[] = [];

  const overdueTasks = getOverdueTasks(workspaceId);
  if (overdueTasks.length > 0) {
    suggestions.push(
      createSuggestion({
        workspaceId,
        type: 'reminder',
        title: '기한 초과 작업 처리',
        description: `${overdueTasks.length}개의 작업이 기한을 초과했습니다. 우선순위를 조정하거나 기한을 연장하세요.`,
        actionType: 'view_overdue_tasks',
        actionPayload: { taskIds: overdueTasks.map((t) => t.id) },
      })
    );
  }

  const priorityTasks = getPriorityTasks(workspaceId);
  const urgentTasks = priorityTasks.filter((t) => t.priority === 'urgent' || t.priority === 'high');
  if (urgentTasks.length > 0) {
    suggestions.push(
      createSuggestion({
        workspaceId,
        type: 'task',
        title: '우선 처리 필요',
        description: `${urgentTasks.length}개의 긴급/높음 우선순위 작업이 대기 중입니다.`,
        actionType: 'view_priority_tasks',
        actionPayload: { taskIds: urgentTasks.map((t) => t.id) },
      })
    );
  }

  const leads = getContactsByStage(workspaceId, 'lead');
  if (leads.length > 0) {
    const oldestLead = leads.sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    )[0];
    const daysSinceCreated = Math.floor(
      (Date.now() - new Date(oldestLead.createdAt).getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysSinceCreated >= 1) {
      suggestions.push(
        createSuggestion({
          workspaceId,
          type: 'follow_up',
          title: '리드 후속 연락',
          description: `${oldestLead.name}님에게 연락한 지 ${daysSinceCreated}일이 지났습니다. 후속 연락을 고려해보세요.`,
          actionType: 'contact_followup',
          actionPayload: { contactId: oldestLead.id },
        })
      );
    }
  }

  const prospects = getContactsByStage(workspaceId, 'qualified');
  if (prospects.length >= 3) {
    suggestions.push(
      createSuggestion({
        workspaceId,
        type: 'optimization',
        title: '잠재고객 전환 기회',
        description: `${prospects.length}명의 잠재고객이 있습니다. 세일즈 활동을 강화하여 고객으로 전환해보세요.`,
        actionType: 'view_prospects',
        actionPayload: { count: prospects.length },
      })
    );
  }

  return suggestions;
}

/**
 * Get assistant home data - combines dashboard info with suggestions
 */
export function getAssistantHome(workspaceId: string): {
  greeting: string;
  suggestions: AssistantSuggestion[];
  quickStats: {
    pendingTasks: number;
    newLeads: number;
    urgentItems: number;
  };
} {
  const tasks = getTasksByWorkspace(workspaceId);
  const pendingTasks = tasks.filter((t) => t.status === 'triage' || t.status === 'backlog' || t.status === 'in_progress');
  const urgentTasks = tasks.filter((t) => t.priority === 'urgent' && t.status !== 'done');
  const overdueTasks = getOverdueTasks(workspaceId);
  const leads = getContactsByStage(workspaceId, 'lead');

  const hour = new Date().getHours();
  let greeting = '안녕하세요!';
  if (hour < 12) greeting = '좋은 아침입니다!';
  else if (hour < 18) greeting = '좋은 오후입니다!';
  else greeting = '좋은 저녁입니다!';

  const activeSuggestions = getActiveSuggestions(workspaceId);

  return {
    greeting,
    suggestions: activeSuggestions.slice(0, 5),
    quickStats: {
      pendingTasks: pendingTasks.length,
      newLeads: leads.length,
      urgentItems: urgentTasks.length + overdueTasks.length,
    },
  };
}
